import React, { FunctionComponent, ReactNode, useMemo } from 'react';

import transparentize from 'polished/lib/color/transparentize';

import { InlineImage } from '@gojob/cms';
import Flex from '@gojob/ui-bits/Flex';
import Text from '@gojob/ui-bits/Text';

import useTheme from '@gojob/ui-bits/styles/useTheme';
import Avatar from './Avatar';

export type QuoteProps = {
  authorName?: string;
  authorRole?: string;
  image?: InlineImage;
  children: ReactNode;
};

const alpha = transparentize(0.5);

const useStyles = () => {
  const { spacing, colors, fonts } = useTheme();

  return useMemo(() => {
    return {
      root: {
        position: 'relative',
        padding: spacing(4),
        backgroundColor: colors.scienceBlue,
        borderRadius: spacing(2),
        width: spacing(48),
        maxWidth: '75vw',
        ':after': {
          position: 'absolute',
          content: '"‘‘"', // that's a lot of quotes 😅
          top: 0,
          left: spacing(1),
          color: alpha(colors.anakiwa),
          ...fonts.headline1,
          letterSpacing: spacing(-0.5),
          lineHeight: 1,
          fontWeight: 800,
          fontSize: spacing(12),
        },
      },
      content: {
        zIndex: 1,
        flexDirection: 'column',
      },
      quote: {
        margin: spacing(0, 0, 2, 0),
        whiteSpace: 'pre-wrap',
        fontWeight: 400,
      },
    } as const;
  }, [spacing, colors, fonts]);
};

const Quote: FunctionComponent<QuoteProps> = (props) => {
  const { authorName = '', authorRole, image, children } = props;

  const classes = useStyles();
  const gatsbyImageData = image?.asset?.localFile?.childImageSharp?.gatsbyImageData;

  return (
    <Flex css={classes.root}>
      <Flex as="article" css={classes.content} flexDirection="column" justifyContent="space-between">
        <Text as="blockquote" variant="message1" css={classes.quote}>
          {children}
        </Text>
        <Flex margin={[0, -2]} flexDirection="row" alignItems="stretch">
          {gatsbyImageData ? (
            <Avatar alt={authorName} image={gatsbyImageData} hotspot={image?.hotspot} />
          ) : null}

          <Flex
            as="footer"
            margin={[0, 2]}
            flexDirection="column"
            justifyContent={['space-between', 'space-evenly']}
          >
            <Text variant="headline4" gutter={0}>
              <strong>{authorName}</strong>
            </Text>
            <Text>{authorRole}</Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Quote;
